import { useEffect, useState, useRef } from 'react';
import { supabase } from './supabaseClient';

const GOLD = '#C9A84C';
const GOLD_LIGHT = '#E8C96A';
const GOLD_PALE = '#F9F1DC';
const GOLD_BORDER = 'rgba(201,168,76,0.2)';
const CREAM = '#FDFBF7';
const CHARCOAL = '#1C1C1E';
const MUTED = '#8A8070';
const WHITE = '#FFFFFF';

const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Jost:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Jost', sans-serif; background: ${CREAM}; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: ${CREAM}; }
  ::-webkit-scrollbar-thumb { background: ${GOLD_BORDER}; border-radius: 4px; }
  .nav-item { transition: all 0.15s; cursor: pointer; }
  .nav-item:hover { background: rgba(201,168,76,0.08) !important; }
  .table-row:hover { background: ${GOLD_PALE} !important; cursor: pointer; }
  .gold-btn { transition: all 0.2s; }
  .gold-btn:hover { opacity: 0.88; transform: translateY(-1px); }
  .card:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(201,168,76,0.15) !important; }
  input, textarea, select { font-family: 'Jost', sans-serif; outline: none; transition: border 0.2s; }
  input:focus, textarea:focus, select:focus { border-color: ${GOLD} !important; box-shadow: 0 0 0 3px rgba(201,168,76,0.1); }
  .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 200; padding: 20px; }
  .modal { background: white; border-radius: 16px; padding: 32px; width: 100%; max-width: 520px; max-height: 90vh; overflow-y: auto; box-shadow: 0 24px 64px rgba(0,0,0,0.18); }
  .tab-btn { transition: all 0.15s; }
  .tab-btn:hover { color: ${GOLD} !important; }
  .people-row:hover { background: ${GOLD_PALE} !important; }
  .import-row:hover { background: ${GOLD_PALE} !important; }
`;

const navItems = [
  { icon: '⬡', label: 'Dashboard' },
  { icon: '♛', label: 'Pageants' },
  { icon: '👥', label: 'People' },
  { icon: '📥', label: 'Import' },
  { icon: '⚙️', label: 'Settings' },
];

// ── Shared Components ──────────────────────────────────────────────────────────

function GoldButton({ children, onClick, outline, disabled, small, style, danger }) {
  return (
    <button className="gold-btn" onClick={onClick} disabled={disabled} style={{
      padding: small ? '7px 14px' : '10px 20px',
      background: danger ? '#E53E3E' : outline ? 'transparent' : `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
      border: `1px solid ${danger ? '#E53E3E' : outline ? GOLD_BORDER : 'transparent'}`,
      borderRadius: '8px', fontSize: small ? '11px' : '13px',
      fontFamily: "'Jost', sans-serif", fontWeight: '600',
      color: outline && !danger ? GOLD : WHITE,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      letterSpacing: '0.04em', ...style,
    }}>{children}</button>
  );
}

function Input({ label, value, onChange, placeholder, type = 'text', required }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      {label && <label style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>{label}{required && ' *'}</label>}
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={{ width: '100%', padding: '11px 14px', border: `1px solid ${GOLD_BORDER}`, borderRadius: '8px', fontSize: '14px', color: CHARCOAL, background: WHITE }} />
    </div>
  );
}

function Select({ label, value, onChange, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      {label && <label style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>{label}</label>}
      <select value={value} onChange={onChange} style={{ width: '100%', padding: '11px 14px', border: `1px solid ${GOLD_BORDER}`, borderRadius: '8px', fontSize: '14px', color: CHARCOAL, background: WHITE, cursor: 'pointer' }}>{children}</select>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    draft:   { bg: '#F3F4F6', text: '#6B7280' },
    active:  { bg: '#E8F5E9', text: '#2E7D32' },
    scoring: { bg: '#FFF8E1', text: '#F57F17' },
    closed:  { bg: '#E8F0FE', text: '#1A56DB' },
  };
  const c = colors[status] || colors.draft;
  return <span style={{ background: c.bg, color: c.text, borderRadius: '20px', padding: '3px 10px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{status}</span>;
}

function SectionHeader({ title, sub, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
      <div>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', color: CHARCOAL, fontWeight: '600' }}>{title}</h2>
        {sub && <p style={{ fontSize: '13px', color: MUTED, marginTop: '3px' }}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}

function EmptyState({ icon, title, desc, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '14px' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>{icon}</div>
      <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: CHARCOAL, marginBottom: '8px' }}>{title}</h3>
      <p style={{ fontSize: '13px', color: MUTED, marginBottom: action ? '20px' : '0', maxWidth: '320px', margin: '0 auto', lineHeight: '1.6' }}>{desc}</p>
      {action && <div style={{ marginTop: '20px' }}>{action}</div>}
    </div>
  );
}

// ── CSV Parsing ────────────────────────────────────────────────────────────────
function parseCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z_]/g, ''));
  return lines.slice(1).map(line => {
    const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    const row = {};
    headers.forEach((h, i) => { row[h] = values[i] || ''; });
    return row;
  }).filter(row => Object.values(row).some(v => v));
}

function downloadCSV(content, filename) {
  const blob = new Blob([content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── DASHBOARD ─────────────────────────────────────────────────────────────────
function Dashboard({ org, pageants, stats, onViewPageant, onNewPageant }) {
  return (
    <div>
      <div style={{ background: CHARCOAL, borderRadius: '16px', padding: '28px 32px', marginBottom: '24px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,76,0.15), transparent 70%)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <p style={{ fontSize: '11px', color: GOLD, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '6px' }}>Director Portal</p>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', color: WHITE, fontWeight: '600', marginBottom: '4px' }}>{org?.name || 'Your Organization'}</h1>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>Score with Confidence. Crown with Peace.</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '28px' }}>
        {[
          { label: 'Total Pageants', value: stats.pageants, icon: '♛' },
          { label: 'Active Now', value: stats.active, icon: '▶' },
          { label: 'Total Judges', value: stats.judges, icon: '⚖️' },
          { label: 'Contestants', value: stats.contestants, icon: '🌟' },
        ].map(s => (
          <div key={s.label} className="card" style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '12px', padding: '18px 20px', transition: 'all 0.2s' }}>
            <div style={{ fontSize: '20px', marginBottom: '8px' }}>{s.icon}</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '32px', color: CHARCOAL, fontWeight: '700', lineHeight: 1, marginBottom: '4px' }}>{s.value}</div>
            <div style={{ fontSize: '12px', color: MUTED }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', color: CHARCOAL }}>Recent Pageants</h2>
        <GoldButton small onClick={onNewPageant}>+ New Pageant</GoldButton>
      </div>

      {pageants.length === 0 ? (
        <EmptyState icon="♛" title="No Pageants Yet" desc="Create your first pageant to get started." action={<GoldButton onClick={onNewPageant}>Create First Pageant</GoldButton>} />
      ) : (
        <div style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '14px', overflow: 'hidden' }}>
          {pageants.slice(0, 5).map((p, i) => (
            <div key={p.id} className="table-row" onClick={() => onViewPageant(p)} style={{ display: 'flex', alignItems: 'center', padding: '14px 20px', borderTop: i > 0 ? `1px solid ${GOLD_BORDER}` : 'none', transition: 'background 0.15s' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: '500', color: CHARCOAL }}>{p.name}</div>
                <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>{p.event_date ? new Date(p.event_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'No date set'}</div>
              </div>
              <StatusBadge status={p.status} />
              <span style={{ marginLeft: '12px', color: GOLD_BORDER, fontSize: '16px' }}>→</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── PAGEANT DETAIL ─────────────────────────────────────────────────────────────
function PageantDetail({ pageant, orgId, userId, onBack, onStatusChange }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [categories, setCategories] = useState([]);
  const [judges, setJudges] = useState([]);
  const [contestants, setContestants] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddJudge, setShowAddJudge] = useState(false);
  const [showAddContestant, setShowAddContestant] = useState(false);
  const [showJoinCode, setShowJoinCode] = useState(false);
  const [catName, setCatName] = useState('');
  const [catWeight, setCatWeight] = useState('');
  const [catMax, setCatMax] = useState('10');
  const [judgeEmail, setJudgeEmail] = useState('');
  const [judgeDisplay, setJudgeDisplay] = useState('');
  const [contEmail, setContEmail] = useState('');
  const [contNumber, setContNumber] = useState('');
  const [contTitle, setContTitle] = useState('');
  const [contPlatform, setContPlatform] = useState('');
  const [error, setError] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [codeCopied, setCodeCopied] = useState(false);

  useEffect(() => { fetchAll(); }, [pageant.id]);

  async function fetchAll() {
    setLoading(true);
    const [catsRes, judgesRes, consRes, pageantRes] = await Promise.all([
      supabase.from('categories').select('*').eq('pageant_id', pageant.id).order('display_order'),
      supabase.from('judges').select('*, users(full_name, email)').eq('pageant_id', pageant.id),
      supabase.from('contestant_profiles').select('*, users(full_name, email)').eq('pageant_id', pageant.id),
      supabase.from('pageants').select('settings').eq('id', pageant.id).single(),
    ]);

    const judgeIds = (judgesRes.data || []).map(j => j.id);
    let scoresData = [];
    if (judgeIds.length > 0) {
      const { data } = await supabase.from('scores').select('*').in('judge_id', judgeIds);
      scoresData = data || [];
    }

    setCategories(catsRes.data || []);
    setJudges(judgesRes.data || []);
    setContestants(consRes.data || []);
    setScores(scoresData);
    setJoinCode(pageantRes.data?.settings?.join_code || '');
    setLoading(false);
  }

  async function updateStatus(newStatus) {
    await supabase.from('pageants').update({ status: newStatus }).eq('id', pageant.id);
    onStatusChange(newStatus);
  }

  async function addCategory() {
    if (!catName || !catWeight) { setError('Name and weight are required'); return; }
    const totalWeight = categories.reduce((sum, c) => sum + parseFloat(c.weight_percent), 0);
    if (totalWeight + parseFloat(catWeight) > 100) { setError(`Total weight would exceed 100% (currently ${totalWeight}%)`); return; }
    setError('');
    await supabase.from('categories').insert({ pageant_id: pageant.id, name: catName, weight_percent: parseFloat(catWeight), max_score: parseFloat(catMax), min_score: 1, scoring_type: 'numeric', display_order: categories.length + 1 });
    setCatName(''); setCatWeight(''); setCatMax('10');
    setShowAddCategory(false);
    fetchAll();
  }

  async function deleteCategory(id) {
    if (!window.confirm('Delete this category?')) return;
    await supabase.from('categories').delete().eq('id', id);
    fetchAll();
  }

  async function addJudge() {
    if (!judgeEmail) { setError('Email is required'); return; }
    setError('');
    const email = judgeEmail.toLowerCase().trim();

    // Try public users table first
    let userId;
    const { data: publicUser } = await supabase.from('users').select('id').eq('email', email).maybeSingle();
    if (publicUser) {
      userId = publicUser.id;
    } else {
      // Try to find in auth.users via RPC or create placeholder
      const { data: inserted } = await supabase.from('users').insert({ id: crypto.randomUUID(), email, full_name: email.split('@')[0] }).select('id').single();
      if (!inserted) { setError('Could not find or create user. Make sure they have a Scorenity account.'); return; }
      userId = inserted.id;
    }

    const { error: memberErr } = await supabase.from('org_members').upsert({ org_id: orgId, user_id: userId, role: 'judge', is_active: true });
    if (memberErr) { setError(memberErr.message); return; }
    const { error: judgeErr } = await supabase.from('judges').upsert({ user_id: userId, pageant_id: pageant.id, display_name: judgeDisplay || null, is_active: true });
    if (judgeErr) { setError(judgeErr.message); return; }
    setJudgeEmail(''); setJudgeDisplay(''); setShowAddJudge(false);
    fetchAll();
  }

  async function removeJudge(judgeId) {
    if (!window.confirm('Remove this judge from the pageant?')) return;
    await supabase.from('judges').update({ is_active: false }).eq('id', judgeId);
    fetchAll();
  }

  async function addContestant() {
    if (!contEmail) { setError('Email is required'); return; }
    setError('');
    const email = contEmail.toLowerCase().trim();

    // Try public users table first
    let userId;
    const { data: publicUser } = await supabase.from('users').select('id').eq('email', email).maybeSingle();
    if (publicUser) {
      userId = publicUser.id;
    } else {
      const { data: inserted } = await supabase.from('users').insert({ id: crypto.randomUUID(), email, full_name: email.split('@')[0] }).select('id').single();
      if (!inserted) { setError('Could not find or create user. Make sure they have a Scorenity account.'); return; }
      userId = inserted.id;
    }

    const { error: memberErr } = await supabase.from('org_members').upsert({ org_id: orgId, user_id: userId, role: 'contestant', is_active: true });
    if (memberErr) { setError(memberErr.message); return; }
    const { error: profError } = await supabase.from('contestant_profiles').upsert({ user_id: userId, pageant_id: pageant.id, contestant_number: contNumber || null, local_title: contTitle || null, platform: contPlatform || null, is_eligible: true, registration_data: {} });
    if (profError) { setError(profError.message); return; }
    setContEmail(''); setContNumber(''); setContTitle(''); setContPlatform('');
    setShowAddContestant(false);
    fetchAll();
  }

  async function removeContestant(profileId) {
    if (!window.confirm('Remove this contestant from the pageant?')) return;
    await supabase.from('contestant_profiles').delete().eq('id', profileId);
    fetchAll();
  }

  async function generateJoinCode() {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    const { data: p } = await supabase.from('pageants').select('settings').eq('id', pageant.id).single();
    await supabase.from('pageants').update({ settings: { ...p.settings, join_code: code } }).eq('id', pageant.id);
    setJoinCode(code);
  }

  function copyCode() {
    navigator.clipboard.writeText(joinCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  }

  const totalWeight = categories.reduce((sum, c) => sum + parseFloat(c.weight_percent), 0);
  const tabs = ['overview', 'categories', 'judges', 'contestants', 'scores', 'join code'];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ background: 'none', border: `1px solid ${GOLD_BORDER}`, borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', color: MUTED, fontFamily: "'Jost', sans-serif" }}>← Back</button>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '24px', color: CHARCOAL }}>{pageant.name}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
            <StatusBadge status={pageant.status} />
            {pageant.event_date && <span style={{ fontSize: '12px', color: MUTED }}>{new Date(pageant.event_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {pageant.status === 'draft' && <GoldButton small onClick={() => updateStatus('active')}>Activate</GoldButton>}
          {pageant.status === 'active' && <GoldButton small onClick={() => updateStatus('scoring')}>Open Scoring</GoldButton>}
          {pageant.status === 'scoring' && <GoldButton small onClick={() => updateStatus('closed')}>Lock Results</GoldButton>}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: `2px solid ${GOLD_BORDER}`, marginBottom: '24px', overflowX: 'auto' }}>
        {tabs.map(tab => (
          <button key={tab} className="tab-btn" onClick={() => setActiveTab(tab)} style={{ padding: '10px 18px', background: 'none', border: 'none', borderBottom: activeTab === tab ? `2px solid ${GOLD}` : '2px solid transparent', marginBottom: '-2px', fontSize: '13px', color: activeTab === tab ? GOLD : MUTED, cursor: 'pointer', fontFamily: "'Jost', sans-serif", fontWeight: activeTab === tab ? '600' : '400', textTransform: 'capitalize', whiteSpace: 'nowrap', letterSpacing: '0.03em' }}>
            {tab}
          </button>
        ))}
      </div>

      {error && <div style={{ background: '#FFF0F0', border: '1px solid #FFCCCC', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#C0392B', marginBottom: '16px' }}>{error}<button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#C0392B' }}>×</button></div>}

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '24px' }}>
            {[
              { label: 'Categories', value: categories.length, icon: '📋', note: `${totalWeight}% total weight` },
              { label: 'Judges', value: judges.filter(j => j.is_active).length, icon: '⚖️', note: 'panel members' },
              { label: 'Contestants', value: contestants.length, icon: '🌟', note: 'registered' },
              { label: 'Scores', value: scores.length, icon: '📊', note: 'submitted' },
            ].map(s => (
              <div key={s.label} style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '12px', padding: '16px 18px' }}>
                <div style={{ fontSize: '20px', marginBottom: '6px' }}>{s.icon}</div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', color: CHARCOAL, fontWeight: '700', lineHeight: 1, marginBottom: '4px' }}>{s.value}</div>
                <div style={{ fontSize: '11px', color: MUTED }}>{s.label}</div>
                <div style={{ fontSize: '11px', color: GOLD, marginTop: '2px' }}>{s.note}</div>
              </div>
            ))}
          </div>

          {totalWeight > 0 && totalWeight !== 100 && (
            <div style={{ background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: '10px', padding: '12px 16px', fontSize: '13px', color: '#F57F17', marginBottom: '16px' }}>
              ⚠️ Category weights total {totalWeight}% — must equal exactly 100% before scoring can open.
            </div>
          )}

          {contestants.length > 0 && judges.filter(j => j.is_active).length > 0 && categories.length > 0 && totalWeight === 100 && pageant.status === 'draft' && (
            <div style={{ background: '#F0FFF4', border: '1px solid #C6F6D5', borderRadius: '10px', padding: '12px 16px', fontSize: '13px', color: '#2E7D32', marginBottom: '16px' }}>
              ✅ Your pageant is ready to activate! You have judges, contestants, and categories all set up.
            </div>
          )}
        </div>
      )}

      {/* CATEGORIES TAB */}
      {activeTab === 'categories' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: CHARCOAL }}>Scoring Categories</h3>
              <p style={{ fontSize: '12px', color: totalWeight === 100 ? '#2E7D32' : MUTED, marginTop: '2px' }}>Total weight: {totalWeight}% {totalWeight === 100 ? '✓' : `(need ${100 - totalWeight}% more)`}</p>
            </div>
            <GoldButton small onClick={() => setShowAddCategory(true)}>+ Add Category</GoldButton>
          </div>

          {showAddCategory && (
            <div style={{ background: GOLD_PALE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
              <h4 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', color: CHARCOAL, marginBottom: '14px' }}>New Category</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px', gap: '12px', alignItems: 'flex-end' }}>
                <Input label="Category Name" value={catName} onChange={e => setCatName(e.target.value)} placeholder="e.g. Evening Gown" />
                <Input label="Weight %" value={catWeight} onChange={e => setCatWeight(e.target.value)} placeholder="25" type="number" />
                <Input label="Max Score" value={catMax} onChange={e => setCatMax(e.target.value)} placeholder="10" type="number" />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <GoldButton small onClick={addCategory}>Add Category</GoldButton>
                <GoldButton small outline onClick={() => { setShowAddCategory(false); setError(''); }}>Cancel</GoldButton>
              </div>
            </div>
          )}

          {categories.length === 0 ? (
            <EmptyState icon="📋" title="No Categories Yet" desc="Add scoring categories with weight percentages that total 100%." action={<GoldButton small onClick={() => setShowAddCategory(true)}>Add First Category</GoldButton>} />
          ) : (
            <div style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '12px', overflow: 'hidden' }}>
              {categories.map((cat, i) => (
                <div key={cat.id} style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', borderTop: i > 0 ? `1px solid ${GOLD_BORDER}` : 'none' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: GOLD_PALE, border: `1px solid ${GOLD_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: GOLD, fontWeight: '700', marginRight: '14px', flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: CHARCOAL }}>{cat.name}</div>
                    <div style={{ fontSize: '12px', color: MUTED }}>Max score: {cat.max_score} · Score range: {cat.min_score}–{cat.max_score}</div>
                  </div>
                  <div style={{ background: GOLD_PALE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '20px', padding: '4px 12px', fontSize: '13px', fontWeight: '600', color: GOLD, marginRight: '12px' }}>{cat.weight_percent}%</div>
                  {pageant.status === 'draft' && <button onClick={() => deleteCategory(cat.id)} style={{ background: 'none', border: 'none', color: '#E53E3E', cursor: 'pointer', fontSize: '16px', padding: '4px' }}>×</button>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* JUDGES TAB */}
      {activeTab === 'judges' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: CHARCOAL }}>Judge Panel</h3>
            <GoldButton small onClick={() => setShowAddJudge(true)}>+ Add Judge</GoldButton>
          </div>

          {showAddJudge && (
            <div style={{ background: GOLD_PALE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
              <h4 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', color: CHARCOAL, marginBottom: '14px' }}>Add Judge</h4>
              <Input label="Judge Email Address *" value={judgeEmail} onChange={e => setJudgeEmail(e.target.value)} placeholder="judge@email.com" type="email" />
              <Input label="Display Name (optional)" value={judgeDisplay} onChange={e => setJudgeDisplay(e.target.value)} placeholder="e.g. Judge A (shown anonymously to contestants)" />
              <p style={{ fontSize: '11px', color: MUTED, marginBottom: '14px' }}>Judge must have a Scorenity account. Their name will be kept anonymous from contestants.</p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <GoldButton small onClick={addJudge}>Add Judge</GoldButton>
                <GoldButton small outline onClick={() => { setShowAddJudge(false); setError(''); }}>Cancel</GoldButton>
              </div>
            </div>
          )}

          {judges.filter(j => j.is_active).length === 0 ? (
            <EmptyState icon="⚖️" title="No Judges Added" desc="Add judges by their email address. They must have a Scorenity account." action={<GoldButton small onClick={() => setShowAddJudge(true)}>Add First Judge</GoldButton>} />
          ) : (
            <div style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '12px', overflow: 'hidden' }}>
              {judges.filter(j => j.is_active).map((judge, i) => (
                <div key={judge.id} className="people-row" style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', borderTop: i > 0 ? `1px solid ${GOLD_BORDER}` : 'none', transition: 'background 0.15s' }}>
                  <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: GOLD_PALE, border: `1px solid ${GOLD_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', color: GOLD, fontFamily: "'Cormorant Garamond', serif", fontWeight: '600', flexShrink: 0, marginRight: '14px' }}>
                    {judge.users?.full_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: CHARCOAL }}>{judge.users?.full_name || 'Unknown'}</div>
                    <div style={{ fontSize: '12px', color: MUTED }}>{judge.users?.email} {judge.display_name && `· Display: ${judge.display_name}`}</div>
                  </div>
                  <span style={{ background: '#E8F0FE', color: '#1A56DB', borderRadius: '20px', padding: '3px 10px', fontSize: '11px', marginRight: '12px' }}>Judge</span>
                  <button onClick={() => removeJudge(judge.id)} style={{ background: 'none', border: 'none', color: '#E53E3E', cursor: 'pointer', fontSize: '16px' }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CONTESTANTS TAB */}
      {activeTab === 'contestants' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: CHARCOAL }}>Contestants</h3>
            <GoldButton small onClick={() => setShowAddContestant(true)}>+ Add Contestant</GoldButton>
          </div>

          {showAddContestant && (
            <div style={{ background: GOLD_PALE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
              <h4 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', color: CHARCOAL, marginBottom: '14px' }}>Add Contestant</h4>
              <Input label="Email Address *" value={contEmail} onChange={e => setContEmail(e.target.value)} placeholder="contestant@email.com" type="email" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <Input label="Contestant Number" value={contNumber} onChange={e => setContNumber(e.target.value)} placeholder="e.g. 14" />
                <Input label="Local Title" value={contTitle} onChange={e => setContTitle(e.target.value)} placeholder="e.g. Miss Fayetteville" />
              </div>
              <Input label="Platform" value={contPlatform} onChange={e => setContPlatform(e.target.value)} placeholder="e.g. Mental Health Awareness" />
              <p style={{ fontSize: '11px', color: MUTED, marginBottom: '14px' }}>Contestant must have a Scorenity account first.</p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <GoldButton small onClick={addContestant}>Add Contestant</GoldButton>
                <GoldButton small outline onClick={() => { setShowAddContestant(false); setError(''); }}>Cancel</GoldButton>
              </div>
            </div>
          )}

          {contestants.length === 0 ? (
            <EmptyState icon="🌟" title="No Contestants Yet" desc="Add contestants by email or share your pageant join code." action={<GoldButton small onClick={() => setShowAddContestant(true)}>Add First Contestant</GoldButton>} />
          ) : (
            <div style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '12px', overflow: 'hidden' }}>
              {contestants.map((c, i) => (
                <div key={c.id} className="people-row" style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', borderTop: i > 0 ? `1px solid ${GOLD_BORDER}` : 'none', transition: 'background 0.15s' }}>
                  <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: GOLD_PALE, border: `1px solid ${GOLD_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', color: GOLD, fontFamily: "'Cormorant Garamond', serif", fontWeight: '600', flexShrink: 0, marginRight: '14px' }}>
                    {c.contestant_number || c.users?.full_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: CHARCOAL }}>{c.users?.full_name || 'Unknown'}</div>
                    <div style={{ fontSize: '12px', color: MUTED }}>{c.local_title && `${c.local_title} · `}{c.platform || c.users?.email}</div>
                  </div>
                  <span style={{ background: '#E8F5E9', color: '#2E7D32', borderRadius: '20px', padding: '3px 10px', fontSize: '11px', marginRight: '12px' }}>#{c.contestant_number || '—'}</span>
                  <button onClick={() => removeContestant(c.id)} style={{ background: 'none', border: 'none', color: '#E53E3E', cursor: 'pointer', fontSize: '16px' }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SCORES TAB */}
      {activeTab === 'scores' && (
        <div>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: CHARCOAL, marginBottom: '16px' }}>Score Summary</h3>
          {scores.length === 0 ? (
            <EmptyState icon="📊" title="No Scores Yet" desc={pageant.status === 'draft' || pageant.status === 'active' ? 'Open scoring to allow judges to begin scoring contestants.' : 'Scores will appear here once judges begin submitting.'} />
          ) : (
            <div>
              {contestants.map(contestant => {
                const contScores = scores.filter(s => s.contestant_profile_id === contestant.id);
                const avg = contScores.length > 0 ? (contScores.reduce((sum, s) => sum + s.raw_score, 0) / contScores.length).toFixed(2) : '—';
                return (
                  <div key={contestant.id} style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '12px', padding: '16px 20px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: CHARCOAL }}>{contestant.users?.full_name}</div>
                        <div style={{ fontSize: '12px', color: MUTED }}>{contestant.local_title} · #{contestant.contestant_number}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', color: GOLD, fontWeight: '700', lineHeight: 1 }}>{avg}</div>
                        <div style={{ fontSize: '11px', color: MUTED }}>avg score</div>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${categories.length}, 1fr)`, gap: '8px' }}>
                      {categories.map(cat => {
                        const catScores = contScores.filter(s => s.category_id === cat.id);
                        const catAvg = catScores.length > 0 ? (catScores.reduce((sum, s) => sum + s.raw_score, 0) / catScores.length).toFixed(1) : '—';
                        return (
                          <div key={cat.id} style={{ background: GOLD_PALE, borderRadius: '8px', padding: '8px 10px', textAlign: 'center' }}>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: GOLD }}>{catAvg}</div>
                            <div style={{ fontSize: '10px', color: MUTED, marginTop: '2px' }}>{cat.name}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* JOIN CODE TAB */}
      {activeTab === 'join code' && (
        <div>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: CHARCOAL, marginBottom: '16px' }}>Pageant Join Code</h3>
          <p style={{ fontSize: '13px', color: MUTED, marginBottom: '20px', lineHeight: '1.7' }}>Share this code with contestants and judges. They enter it when creating their Scorenity account and are automatically added to your pageant.</p>

          <div style={{ background: CHARCOAL, borderRadius: '16px', padding: '32px', textAlign: 'center', marginBottom: '20px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '200px', height: '200px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,76,0.1), transparent 70%)' }} />
            <div style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '8px' }}>Join Code</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '56px', fontWeight: '700', color: GOLD, letterSpacing: '0.2em', lineHeight: 1, position: 'relative', zIndex: 1 }}>{joinCode || '——'}</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
            <button onClick={copyCode} style={{ padding: '11px', background: GOLD_PALE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '8px', fontSize: '12px', color: GOLD, cursor: 'pointer', fontFamily: "'Jost', sans-serif", fontWeight: '600' }}>
              {codeCopied ? '✓ Copied!' : '📋 Copy Code'}
            </button>
            <button onClick={() => { navigator.clipboard.writeText(`Join my pageant on Scorenity! Code: ${joinCode} — app.scorenity.com`); setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000); }} style={{ padding: '11px', background: GOLD_PALE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '8px', fontSize: '12px', color: GOLD, cursor: 'pointer', fontFamily: "'Jost', sans-serif", fontWeight: '600' }}>
              🔗 Copy Link
            </button>
            <button onClick={generateJoinCode} style={{ padding: '11px', background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '8px', fontSize: '12px', color: MUTED, cursor: 'pointer', fontFamily: "'Jost', sans-serif" }}>
              🔄 New Code
            </button>
          </div>

          <div style={{ background: GOLD_PALE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '10px', padding: '14px' }}>
            <div style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>How it works</div>
            {['Share the code or link with contestants and judges', 'They create a Scorenity account at app.scorenity.com', 'They enter the code and are added to your pageant automatically', 'You can see them in the Contestants and Judges tabs'].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '4px 0', fontSize: '12px', color: CHARCOAL }}>
                <span style={{ color: GOLD, fontWeight: '700', flexShrink: 0 }}>{i + 1}.</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── PEOPLE MANAGEMENT ──────────────────────────────────────────────────────────
function PeopleManagement({ orgId }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('contestant');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [activeMenu, setActiveMenu] = useState(null);

  useEffect(() => { fetchMembers(); }, []);

  async function fetchMembers() {
    setLoading(true);
    const { data } = await supabase.from('org_members').select('*, users(id, full_name, email)').eq('org_id', orgId).order('joined_at', { ascending: false });
    setMembers(data || []);
    setLoading(false);
  }

  async function handleInvite() {
    if (!inviteEmail.trim()) { setInviteError('Email is required'); return; }
    setInviteLoading(true); setInviteError('');
    const email = inviteEmail.toLowerCase().trim();

    let userId;
    const { data: publicUser } = await supabase.from('users').select('id').eq('email', email).maybeSingle();
    if (publicUser) {
      userId = publicUser.id;
    } else {
      const { data: inserted } = await supabase.from('users').insert({ id: crypto.randomUUID(), email, full_name: email.split('@')[0] }).select('id').single();
      if (!inserted) { setInviteError('Could not find or create user. Make sure they have a Scorenity account at app.scorenity.com.'); setInviteLoading(false); return; }
      userId = inserted.id;
    }

    const { error } = await supabase.from('org_members').upsert({ org_id: orgId, user_id: userId, role: inviteRole, is_active: true });
    if (error) { setInviteError(error.message); } else {
      setInviteSuccess(`${email} added as ${inviteRole}!`);
      setInviteEmail(''); setInviteRole('contestant');
      fetchMembers();
      setTimeout(() => { setInviteSuccess(''); setShowInvite(false); }, 2000);
    }
    setInviteLoading(false);
  }

  async function updateRole(memberId, newRole) {
    await supabase.from('org_members').update({ role: newRole }).eq('id', memberId);
    fetchMembers(); setActiveMenu(null);
  }

  async function toggleActive(memberId, isActive) {
    await supabase.from('org_members').update({ is_active: !isActive }).eq('id', memberId);
    fetchMembers(); setActiveMenu(null);
  }

  const filtered = members.filter(m => {
    const matchSearch = !search || m.users?.full_name?.toLowerCase().includes(search.toLowerCase()) || m.users?.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = filterRole === 'all' || m.role === filterRole;
    return matchSearch && matchRole;
  });

  const roleColors = { director: '#F57F17', judge: '#1A56DB', contestant: '#2E7D32', auditor: '#6B7280' };
  const roleBg = { director: '#FFF8E1', judge: '#E8F0FE', contestant: '#E8F5E9', auditor: '#F3F4F6' };

  return (
    <div>
      <SectionHeader title="People" sub={`${members.filter(m => m.is_active).length} active members`} action={<GoldButton small onClick={() => setShowInvite(true)}>+ Add Person</GoldButton>} />

      {showInvite && (
        <div style={{ background: GOLD_PALE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
          <h4 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', color: CHARCOAL, marginBottom: '14px' }}>Add Person to Organization</h4>
          {inviteError && <div style={{ background: '#FFF0F0', border: '1px solid #FFCCCC', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: '#C0392B', marginBottom: '12px' }}>{inviteError}</div>}
          {inviteSuccess && <div style={{ background: '#F0FFF4', border: '1px solid #C6F6D5', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: '#2E7D32', marginBottom: '12px' }}>✅ {inviteSuccess}</div>}
          <Input label="Email Address *" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="their@email.com" type="email" />
          <Select label="Role" value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
            <option value="contestant">Contestant</option>
            <option value="judge">Judge</option>
            <option value="director">Director</option>
            <option value="auditor">Auditor</option>
          </Select>
          <p style={{ fontSize: '11px', color: MUTED, marginBottom: '14px' }}>Person must have a Scorenity account at app.scorenity.com first.</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <GoldButton small onClick={handleInvite} disabled={inviteLoading}>{inviteLoading ? 'Adding...' : 'Add Person'}</GoldButton>
            <GoldButton small outline onClick={() => { setShowInvite(false); setInviteError(''); }}>Cancel</GoldButton>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
        <input placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, padding: '10px 14px', border: `1px solid ${GOLD_BORDER}`, borderRadius: '8px', fontSize: '14px', color: CHARCOAL, background: WHITE, outline: 'none', fontFamily: "'Jost', sans-serif" }} />
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ padding: '10px 14px', border: `1px solid ${GOLD_BORDER}`, borderRadius: '8px', fontSize: '13px', color: CHARCOAL, background: WHITE, cursor: 'pointer', fontFamily: "'Jost', sans-serif", minWidth: '130px' }}>
          <option value="all">All Roles</option>
          <option value="director">Directors</option>
          <option value="judge">Judges</option>
          <option value="contestant">Contestants</option>
          <option value="auditor">Auditors</option>
        </select>
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: MUTED }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon="👥" title="No Members Found" desc="Add people to your organization using the button above." action={<GoldButton small onClick={() => setShowInvite(true)}>+ Add Person</GoldButton>} />
      ) : (
        <div style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '12px', overflow: 'hidden' }}>
          {filtered.map((member, i) => (
            <div key={member.id} className="people-row" style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', borderTop: i > 0 ? `1px solid ${GOLD_BORDER}` : 'none', transition: 'background 0.15s', opacity: member.is_active ? 1 : 0.5, position: 'relative' }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: GOLD_PALE, border: `1px solid ${GOLD_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', color: GOLD, fontFamily: "'Cormorant Garamond', serif", fontWeight: '600', flexShrink: 0, marginRight: '14px' }}>
                {member.users?.full_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: '500', color: CHARCOAL, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.users?.full_name || 'Unknown'}</div>
                <div style={{ fontSize: '11px', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.users?.email}</div>
              </div>
              <span style={{ background: roleBg[member.role], color: roleColors[member.role], borderRadius: '20px', padding: '3px 10px', fontSize: '11px', fontWeight: '500', marginRight: '12px', flexShrink: 0 }}>
                {member.role}
              </span>
              <div style={{ position: 'relative' }}>
                <button onClick={() => setActiveMenu(activeMenu === member.id ? null : member.id)} style={{ background: 'none', border: `1px solid ${GOLD_BORDER}`, borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', fontSize: '16px', color: MUTED }}>⋮</button>
                {activeMenu === member.id && (
                  <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: '4px', background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 50, minWidth: '180px', overflow: 'hidden' }}>
                    <div style={{ padding: '8px', borderBottom: `1px solid ${GOLD_BORDER}` }}>
                      <div style={{ fontSize: '10px', color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px', padding: '0 6px' }}>Change Role</div>
                      {['director', 'judge', 'contestant', 'auditor'].map(r => (
                        <button key={r} onClick={() => updateRole(member.id, r)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: member.role === r ? GOLD_PALE : 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: member.role === r ? GOLD : CHARCOAL, borderRadius: '6px', fontFamily: "'Jost', sans-serif" }}>
                          {r.charAt(0).toUpperCase() + r.slice(1)} {member.role === r && '✓'}
                        </button>
                      ))}
                    </div>
                    <div style={{ padding: '6px 8px' }}>
                      <button onClick={() => toggleActive(member.id, member.is_active)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: member.is_active ? '#E53E3E' : '#2E7D32', borderRadius: '6px', fontFamily: "'Jost', sans-serif" }}>
                        {member.is_active ? '✕ Deactivate' : '✓ Reactivate'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── IMPORT TOOLS ───────────────────────────────────────────────────────────────
function ImportTools({ orgId, pageants }) {
  const [importType, setImportType] = useState('contestants');
  const [selectedPageant, setSelectedPageant] = useState('');
  const [rows, setRows] = useState([]);
  const [stage, setStage] = useState('upload');
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const fileRef = useRef();

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const parsed = parseCSV(ev.target.result);
      const validated = parsed.map((row, i) => {
        const errs = [];
        if (!row.full_name && !row.name) errs.push('Missing name');
        if (!row.email || !row.email.includes('@')) errs.push('Invalid email');
        return { ...row, full_name: row.full_name || row.name, _index: i + 1, _errors: errs, _selected: errs.length === 0 };
      });
      setRows(validated);
      setStage('preview');
    };
    reader.readAsText(file);
  }

  async function handleImport() {
    if (!selectedPageant && importType !== 'members') { alert('Please select a pageant first'); return; }
    setStage('importing');
    const toImport = rows.filter(r => r._selected && r._errors.length === 0);
    let success = 0, failed = 0, skipped = 0;

    for (let i = 0; i < toImport.length; i++) {
      const row = toImport[i];
      setProgress(Math.round(((i + 1) / toImport.length) * 100));
      try {
        const { data: user } = await supabase.from('users').select('id').eq('email', row.email.toLowerCase()).single();
        if (!user) { skipped++; continue; }

        const role = importType === 'judges' ? 'judge' : 'contestant';
        await supabase.from('org_members').upsert({ org_id: orgId, user_id: user.id, role, is_active: true });

        if (importType === 'judges') {
          await supabase.from('judges').upsert({ user_id: user.id, pageant_id: selectedPageant, display_name: row.display_name || null, is_active: true });
        } else {
          await supabase.from('contestant_profiles').upsert({ user_id: user.id, pageant_id: selectedPageant, contestant_number: row.contestant_number || row.number || null, local_title: row.local_title || row.title || null, platform: row.platform || null, is_eligible: true, registration_data: {} });
        }
        success++;
      } catch { failed++; }
    }
    setResults({ success, failed, skipped });
    setStage('done');
  }

  const contestantTemplate = 'Full Name,Email,Contestant Number,Local Title,Platform\nJane Smith,jane@email.com,14,Miss Fayetteville,Mental Health Awareness';
  const judgeTemplate = 'Full Name,Email,Display Name\nJohn Doe,john@email.com,Judge A';

  return (
    <div>
      <SectionHeader title="Import Tools" sub="Bulk import contestants and judges from CSV" />

      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        {['contestants', 'judges'].map(type => (
          <button key={type} onClick={() => { setImportType(type); setStage('upload'); setRows([]); }} style={{ padding: '10px 20px', background: importType === type ? `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})` : WHITE, border: `1px solid ${importType === type ? 'transparent' : GOLD_BORDER}`, borderRadius: '8px', fontSize: '13px', color: importType === type ? WHITE : MUTED, cursor: 'pointer', fontFamily: "'Jost', sans-serif", fontWeight: '600', textTransform: 'capitalize' }}>
            Import {type}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>Select Pageant *</label>
        <select value={selectedPageant} onChange={e => setSelectedPageant(e.target.value)} style={{ width: '100%', padding: '11px 14px', border: `1px solid ${GOLD_BORDER}`, borderRadius: '8px', fontSize: '14px', color: CHARCOAL, background: WHITE, cursor: 'pointer', fontFamily: "'Jost', sans-serif" }}>
          <option value="">Choose a pageant...</option>
          {pageants.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {stage === 'upload' && (
        <div>
          <div style={{ background: GOLD_PALE, border: `2px dashed ${GOLD_BORDER}`, borderRadius: '12px', padding: '40px', textAlign: 'center', marginBottom: '16px', cursor: 'pointer' }} onClick={() => fileRef.current?.click()}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📄</div>
            <p style={{ fontSize: '14px', color: CHARCOAL, fontWeight: '500', marginBottom: '4px' }}>Click to upload your CSV file</p>
            <p style={{ fontSize: '12px', color: MUTED }}>Drag and drop also works</p>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} style={{ display: 'none' }} />
          </div>
          <button onClick={() => downloadCSV(importType === 'judges' ? judgeTemplate : contestantTemplate, `scorenity-${importType}-template.csv`)} style={{ background: 'none', border: `1px solid ${GOLD_BORDER}`, borderRadius: '8px', padding: '8px 16px', fontSize: '12px', color: GOLD, cursor: 'pointer', fontFamily: "'Jost', sans-serif" }}>
            ↓ Download {importType} template
          </button>
        </div>
      )}

      {stage === 'preview' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '13px', color: CHARCOAL }}>{rows.filter(r => r._selected).length} of {rows.length} rows ready</span>
            <GoldButton small outline onClick={() => { setStage('upload'); setRows([]); }}>← Back</GoldButton>
          </div>
          <div style={{ maxHeight: '300px', overflowY: 'auto', border: `1px solid ${GOLD_BORDER}`, borderRadius: '10px', overflow: 'hidden', marginBottom: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '30px 1fr 1fr 100px', padding: '8px 14px', background: CREAM, fontSize: '10px', color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', position: 'sticky', top: 0 }}>
              <span></span><span>Name</span><span>Email</span><span>Status</span>
            </div>
            {rows.map((row, i) => (
              <div key={i} className="import-row" style={{ display: 'grid', gridTemplateColumns: '30px 1fr 1fr 100px', padding: '10px 14px', borderTop: `1px solid ${GOLD_BORDER}`, alignItems: 'center', background: row._errors.length > 0 ? '#FFF8F8' : WHITE, transition: 'background 0.1s' }}>
                <input type="checkbox" checked={row._selected && row._errors.length === 0} onChange={e => setRows(rows.map((r, j) => j === i ? { ...r, _selected: e.target.checked } : r))} disabled={row._errors.length > 0} style={{ accentColor: GOLD }} />
                <span style={{ fontSize: '13px', color: CHARCOAL }}>{row.full_name || '—'}</span>
                <span style={{ fontSize: '12px', color: MUTED }}>{row.email || '—'}</span>
                <span>{row._errors.length > 0 ? <span style={{ fontSize: '10px', background: '#FFF0F0', color: '#E53E3E', borderRadius: '20px', padding: '2px 8px' }}>⚠ Error</span> : <span style={{ fontSize: '10px', background: '#E8F5E9', color: '#2E7D32', borderRadius: '20px', padding: '2px 8px' }}>✓ Ready</span>}</span>
              </div>
            ))}
          </div>
          <GoldButton onClick={handleImport} disabled={!selectedPageant}>Import {rows.filter(r => r._selected && r._errors.length === 0).length} {importType} →</GoldButton>
        </div>
      )}

      {stage === 'importing' && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '32px', marginBottom: '16px' }}>⏳</div>
          <p style={{ fontSize: '14px', color: CHARCOAL, marginBottom: '12px' }}>Importing {importType}...</p>
          <div style={{ height: '6px', background: '#F0EBE0', borderRadius: '3px', overflow: 'hidden', maxWidth: '300px', margin: '0 auto' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: `linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT})`, borderRadius: '3px', transition: 'width 0.3s' }} />
          </div>
          <p style={{ fontSize: '12px', color: MUTED, marginTop: '8px' }}>{progress}%</p>
        </div>
      )}

      {stage === 'done' && results && (
        <div style={{ textAlign: 'center', padding: '32px', background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '14px' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>✅</div>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', color: CHARCOAL, marginBottom: '16px' }}>Import Complete</h3>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '20px' }}>
            <div style={{ background: '#E8F5E9', borderRadius: '10px', padding: '12px 20px' }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', color: '#2E7D32', fontWeight: '700' }}>{results.success}</div>
              <div style={{ fontSize: '11px', color: '#2E7D32' }}>Imported</div>
            </div>
            {results.skipped > 0 && (
              <div style={{ background: '#FFF8E1', borderRadius: '10px', padding: '12px 20px' }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', color: '#F57F17', fontWeight: '700' }}>{results.skipped}</div>
                <div style={{ fontSize: '11px', color: '#F57F17' }}>No Account</div>
              </div>
            )}
            {results.failed > 0 && (
              <div style={{ background: '#FFF0F0', borderRadius: '10px', padding: '12px 20px' }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', color: '#E53E3E', fontWeight: '700' }}>{results.failed}</div>
                <div style={{ fontSize: '11px', color: '#E53E3E' }}>Failed</div>
              </div>
            )}
          </div>
          {results.skipped > 0 && <p style={{ fontSize: '12px', color: MUTED, marginBottom: '16px' }}>Skipped rows mean that email doesn't have a Scorenity account yet. Ask them to sign up first.</p>}
          <GoldButton onClick={() => { setStage('upload'); setRows([]); setResults(null); }}>Import More</GoldButton>
        </div>
      )}
    </div>
  );
}

// ── ORG SETTINGS ───────────────────────────────────────────────────────────────
function OrgSettings({ orgId, onUpdated }) {
  const [org, setOrg] = useState(null);
  const [name, setName] = useState('');
  const [state, setState] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { fetchOrg(); }, []);

  async function fetchOrg() {
    const { data } = await supabase.from('organizations').select('*').eq('id', orgId).single();
    if (data) { setOrg(data); setName(data.name || ''); setState(data.settings?.state || ''); setTimezone(data.settings?.timezone || 'America/New_York'); }
  }

  async function handleSave() {
    if (!name.trim()) { setError('Organization name is required'); return; }
    setSaving(true); setError('');
    const { error: err } = await supabase.from('organizations').update({ name: name.trim(), settings: { ...org?.settings, state, timezone } }).eq('id', orgId);
    if (err) { setError(err.message); } else { setSaved(true); onUpdated?.(); setTimeout(() => setSaved(false), 2000); }
    setSaving(false);
  }

  return (
    <div>
      <SectionHeader title="Organization Settings" sub="Manage your organization details" />
      <div style={{ maxWidth: '560px' }}>
        <div style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '14px', padding: '24px', marginBottom: '20px' }}>
          {error && <div style={{ background: '#FFF0F0', border: '1px solid #FFCCCC', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#C0392B', marginBottom: '14px' }}>{error}</div>}
          <Input label="Organization Name *" value={name} onChange={e => setName(e.target.value)} placeholder="Your organization name" />
          <Input label="State" value={state} onChange={e => setState(e.target.value)} placeholder="e.g. North Carolina" />
          <Select label="Timezone" value={timezone} onChange={e => setTimezone(e.target.value)}>
            <option value="America/New_York">Eastern (ET)</option>
            <option value="America/Chicago">Central (CT)</option>
            <option value="America/Denver">Mountain (MT)</option>
            <option value="America/Los_Angeles">Pacific (PT)</option>
          </Select>
          <div style={{ marginTop: '4px' }}>
            <label style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>Organization ID</label>
            <div style={{ padding: '11px 14px', border: `1px solid ${GOLD_BORDER}`, borderRadius: '8px', fontSize: '12px', color: MUTED, background: CREAM, fontFamily: 'monospace' }}>{orgId}</div>
          </div>
          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
            <GoldButton onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save Changes'}</GoldButton>
          </div>
        </div>

        <div style={{ background: WHITE, border: '1px solid #FFCCCC', borderRadius: '14px', padding: '20px' }}>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', color: '#C0392B', marginBottom: '8px' }}>Danger Zone</h3>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: CHARCOAL }}>Delete Organization</div>
              <div style={{ fontSize: '12px', color: MUTED }}>Permanently deletes all pageants, scores, and data. Cannot be undone.</div>
            </div>
            <GoldButton small danger onClick={() => { if (window.confirm('Are you absolutely sure? This cannot be undone.')) { alert('Please contact support to delete your organization.'); } }}>Delete</GoldButton>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── CREATE PAGEANT MODAL ───────────────────────────────────────────────────────
function CreatePageantModal({ orgId, userId, onCreated, onClose }) {
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [template, setTemplate] = useState('classic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const templates = {
    classic: [{ name: 'Interview', weight: 25 }, { name: 'Evening Gown', weight: 25 }, { name: 'Talent', weight: 25 }, { name: 'On-Stage Question', weight: 25 }],
    beauty:  [{ name: 'Interview', weight: 20 }, { name: 'Evening Gown', weight: 35 }, { name: 'Swimsuit', weight: 30 }, { name: 'On-Stage Question', weight: 15 }],
    talent:  [{ name: 'Interview', weight: 20 }, { name: 'Evening Gown', weight: 20 }, { name: 'Talent', weight: 40 }, { name: 'On-Stage Question', weight: 20 }],
    blank:   [],
  };

  async function handleCreate() {
    if (!name.trim()) { setError('Pageant name is required'); return; }
    setLoading(true); setError('');
    const { data: pageant, error: pErr } = await supabase.from('pageants').insert({ org_id: orgId, name: name.trim(), event_date: date || null, status: 'draft', created_by: userId, settings: {} }).select().single();
    if (pErr) { setError(pErr.message); setLoading(false); return; }
    if (templates[template].length > 0) {
      await supabase.from('categories').insert(templates[template].map((c, i) => ({ pageant_id: pageant.id, name: c.name, weight_percent: c.weight, max_score: 10, min_score: 1, scoring_type: 'numeric', display_order: i + 1 })));
    }
    setLoading(false);
    onCreated(pageant);
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', color: CHARCOAL }}>New Pageant</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', color: MUTED, cursor: 'pointer' }}>×</button>
        </div>
        {error && <div style={{ background: '#FFF0F0', border: '1px solid #FFCCCC', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#C0392B', marginBottom: '14px' }}>{error}</div>}
        <Input label="Pageant Name *" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Miss Carolina Classic 2025" />
        <Input label="Event Date" value={date} onChange={e => setDate(e.target.value)} type="date" />
        <div style={{ marginBottom: '14px' }}>
          <label style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>Scoring Template</label>
          {[
            { id: 'classic', label: 'Classic 4-Category', desc: 'Interview, Gown, Talent, Question (25% each)' },
            { id: 'beauty',  label: 'Beauty Focus', desc: 'Interview 20%, Gown 35%, Swimsuit 30%, Question 15%' },
            { id: 'talent',  label: 'Talent Heavy', desc: 'Interview 20%, Gown 20%, Talent 40%, Question 20%' },
            { id: 'blank',   label: 'Start Blank', desc: 'Add your own custom categories' },
          ].map(t => (
            <div key={t.id} onClick={() => setTemplate(t.id)} style={{ padding: '12px 14px', border: `1px solid ${template === t.id ? GOLD : GOLD_BORDER}`, borderRadius: '8px', marginBottom: '8px', cursor: 'pointer', background: template === t.id ? GOLD_PALE : WHITE, transition: 'all 0.15s' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: template === t.id ? GOLD : CHARCOAL }}>{t.label}</div>
              <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>{t.desc}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <GoldButton outline onClick={onClose}>Cancel</GoldButton>
          <GoldButton onClick={handleCreate} disabled={loading}>{loading ? 'Creating...' : 'Create Pageant →'}</GoldButton>
        </div>
      </div>
    </div>
  );
}

// ── MAIN DIRECTOR PORTAL ───────────────────────────────────────────────────────
export default function DirectorPortal({ user, onSignOut, onSwitchRole }) {
  const [activeNav, setActiveNav] = useState('Dashboard');
  const [org, setOrg] = useState(null);
  const [pageants, setPageants] = useState([]);
  const [selectedPageant, setSelectedPageant] = useState(null);
  const [stats, setStats] = useState({ pageants: 0, active: 0, judges: 0, contestants: 0 });
  const [showCreatePageant, setShowCreatePageant] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    const { data: memberData } = await supabase.from('org_members').select('org_id').eq('user_id', user.id).eq('role', 'director').eq('is_active', true).single();
    if (!memberData) { setLoading(false); return; }
    const orgId = memberData.org_id;
    const [orgRes, pageantRes] = await Promise.all([
      supabase.from('organizations').select('*').eq('id', orgId).single(),
      supabase.from('pageants').select('*').eq('org_id', orgId).order('created_at', { ascending: false }),
    ]);
    setOrg(orgRes.data);
    const pageantList = pageantRes.data || [];
    setPageants(pageantList);
    const pageantIds = pageantList.map(p => p.id);
    let judgesCount = 0, contestantsCount = 0;
    if (pageantIds.length > 0) {
      const [jRes, cRes] = await Promise.all([
        supabase.from('judges').select('id', { count: 'exact', head: true }).in('pageant_id', pageantIds).eq('is_active', true),
        supabase.from('contestant_profiles').select('id', { count: 'exact', head: true }).in('pageant_id', pageantIds),
      ]);
      judgesCount = jRes.count || 0;
      contestantsCount = cRes.count || 0;
    }
    setStats({ pageants: pageantList.length, active: pageantList.filter(p => ['active', 'scoring'].includes(p.status)).length, judges: judgesCount, contestants: contestantsCount });
    setLoading(false);
  }

  function handlePageantCreated(pageant) {
    setShowCreatePageant(false);
    fetchData();
    setSelectedPageant(pageant);
    setActiveNav('Pageants');
  }

  function handleStatusChange(newStatus) {
    setSelectedPageant(prev => ({ ...prev, status: newStatus }));
    fetchData();
  }

  const orgId = org?.id;
  const userId = user?.id;

  const sidebar = (
    <div style={{ width: '220px', minWidth: '220px', background: CHARCOAL, height: '100vh', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0 }}>
      {/* Logo */}
      <div style={{ padding: '24px 20px', borderBottom: '1px solid rgba(201,168,76,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>♛</div>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', color: WHITE, fontWeight: '600', lineHeight: 1 }}>Scorenity</div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', marginTop: '2px', letterSpacing: '0.08em' }}>Director Portal</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <div style={{ flex: 1, padding: '12px 10px', overflowY: 'auto' }}>
        {navItems.map(item => (
          <div key={item.label} className="nav-item" onClick={() => { setActiveNav(item.label); setSelectedPageant(null); setSidebarOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', marginBottom: '2px', background: activeNav === item.label && !selectedPageant ? `linear-gradient(90deg, ${GOLD}22, ${GOLD}11)` : 'transparent', border: activeNav === item.label && !selectedPageant ? `1px solid ${GOLD}44` : '1px solid transparent' }}>
            <span style={{ fontSize: '15px', width: '18px', textAlign: 'center', color: activeNav === item.label && !selectedPageant ? GOLD : '#666' }}>{item.icon}</span>
            <span style={{ fontSize: '13px', color: activeNav === item.label && !selectedPageant ? GOLD : 'rgba(255,255,255,0.5)', fontWeight: activeNav === item.label && !selectedPageant ? '500' : '400', letterSpacing: '0.02em' }}>{item.label}</span>
          </div>
        ))}
      </div>

      {/* User */}
      <div style={{ padding: '16px 14px', borderTop: '1px solid rgba(201,168,76,0.15)' }}>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
          {onSwitchRole && (
            <button onClick={onSwitchRole} style={{ background: 'none', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '6px', padding: '5px 10px', fontSize: '11px', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontFamily: "'Jost', sans-serif" }}>Switch Role</button>
          )}
          <button onClick={onSignOut} style={{ background: 'none', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '6px', padding: '5px 10px', fontSize: '11px', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontFamily: "'Jost', sans-serif" }}>Sign Out</button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <>
        <style>{globalStyles}</style>
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: CREAM }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '52px', height: '52px', background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, borderRadius: '13px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', marginBottom: '12px' }}>♛</div>
            <p style={{ color: GOLD, fontSize: '12px', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Loading...</p>
          </div>
        </div>
      </>
    );
  }

  if (!org) {
    return (
      <>
        <style>{globalStyles}</style>
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: CREAM, padding: '24px' }}>
          <div style={{ textAlign: 'center', maxWidth: '420px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>♛</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', color: CHARCOAL, marginBottom: '12px' }}>Welcome to Scorenity</h2>
            <p style={{ fontSize: '14px', color: MUTED, lineHeight: '1.7', marginBottom: '24px' }}>Your account doesn't have an organization yet. Contact your administrator or reach out to info@scorenity.com to get set up.</p>
            <button onClick={onSignOut} style={{ background: 'none', border: `1px solid ${GOLD_BORDER}`, borderRadius: '10px', padding: '12px 24px', color: GOLD, fontFamily: "'Jost', sans-serif", fontSize: '13px', cursor: 'pointer' }}>Sign Out</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{globalStyles}</style>

      {/* Mobile header */}
      <div style={{ display: 'none', position: 'fixed', top: 0, left: 0, right: 0, height: '60px', background: CHARCOAL, zIndex: 100, alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', borderBottom: '1px solid rgba(201,168,76,0.15)' }} id="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '32px', height: '32px', background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>♛</div>
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', color: WHITE, fontWeight: '600' }}>Scorenity</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', color: WHITE, fontSize: '22px', cursor: 'pointer' }}>☰</button>
      </div>

      <div style={{ display: 'flex', minHeight: '100vh', background: CREAM }}>
        {sidebar}

        {/* Main content */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <div style={{ maxWidth: '1000px', padding: '32px 28px', margin: '0 auto' }}>

            {/* New pageant button in header */}
            {(activeNav === 'Dashboard' || activeNav === 'Pageants') && !selectedPageant && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                <GoldButton onClick={() => setShowCreatePageant(true)}>+ New Pageant</GoldButton>
              </div>
            )}

            {/* Pageant detail */}
            {selectedPageant && (
              <PageantDetail pageant={selectedPageant} orgId={orgId} userId={userId} onBack={() => { setSelectedPageant(null); fetchData(); }} onStatusChange={handleStatusChange} />
            )}

            {/* Dashboard */}
            {!selectedPageant && activeNav === 'Dashboard' && (
              <Dashboard org={org} pageants={pageants} stats={stats} onViewPageant={p => { setSelectedPageant(p); setActiveNav('Pageants'); }} onNewPageant={() => setShowCreatePageant(true)} />
            )}

            {/* Pageants list */}
            {!selectedPageant && activeNav === 'Pageants' && (
              <div>
                <SectionHeader title="Pageants" sub={`${pageants.length} total pageants`} action={<GoldButton small onClick={() => setShowCreatePageant(true)}>+ New Pageant</GoldButton>} />
                {pageants.length === 0 ? (
                  <EmptyState icon="♛" title="No Pageants Yet" desc="Create your first pageant to get started with digital scoring." action={<GoldButton onClick={() => setShowCreatePageant(true)}>Create First Pageant</GoldButton>} />
                ) : (
                  <div style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '14px', overflow: 'hidden' }}>
                    {pageants.map((p, i) => (
                      <div key={p.id} className="table-row" onClick={() => setSelectedPageant(p)} style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderTop: i > 0 ? `1px solid ${GOLD_BORDER}` : 'none', transition: 'background 0.15s' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: '500', color: CHARCOAL, marginBottom: '3px' }}>{p.name}</div>
                          <div style={{ fontSize: '12px', color: MUTED }}>{p.event_date ? new Date(p.event_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'No date set'}</div>
                        </div>
                        <StatusBadge status={p.status} />
                        <span style={{ marginLeft: '14px', color: GOLD_BORDER, fontSize: '18px' }}>→</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* People */}
            {!selectedPageant && activeNav === 'People' && <PeopleManagement orgId={orgId} />}

            {/* Import */}
            {!selectedPageant && activeNav === 'Import' && <ImportTools orgId={orgId} pageants={pageants} />}

            {/* Settings */}
            {!selectedPageant && activeNav === 'Settings' && <OrgSettings orgId={orgId} onUpdated={fetchData} />}

          </div>
        </div>
      </div>

      {showCreatePageant && <CreatePageantModal orgId={orgId} userId={userId} onCreated={handlePageantCreated} onClose={() => setShowCreatePageant(false)} />}
    </>
  );
}